<!-- src/components/stock/StockRecipeSales.vue -->
<template>
  <section class="recipe-sales-card">
    <button
      class="recipe-sales-header"
      type="button"
      :aria-expanded="open"
      @click="open = !open"
    >
      <span class="recipe-sales-title">
        <i class="material-icons"> local_bar </i>

        <span>
          <strong> Recipe Sales </strong>

          <small> Enter the number of each cocktail or recipe sold </small>
        </span>
      </span>

      <span class="recipe-sales-header-right">
        <span v-if="activeRecipeCount" class="recipe-count-chip">
          {{ activeRecipeCount }} entered
        </span>

        <i class="material-icons">
          {{ open ? "expand_less" : "expand_more" }}
        </i>
      </span>
    </button>

    <div v-show="open" class="recipe-sales-body">
      <div v-if="!recipes.length" class="core-empty-state">
        No recipes are available. Operations must create them in
        <strong>Stock Database → Stock Recipes</strong> first.
      </div>

      <div v-else class="table-wrap recipe-sales-table-wrap">
        <table class="core-table recipe-sales-table">
          <thead>
            <tr>
              <th>Recipe</th>

              <th>Ingredient Usage per Sale</th>

              <th class="right-align">Quantity Sold</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="recipe in recipes" :key="recipe.id">
              <td class="recipe-name">
                {{ recipe.recipe_name }}
              </td>

              <td>
                <div class="ingredient-summary">
                  <span
                    v-for="ingredient in recipe.ingredients"
                    :key="ingredient.product_key"
                  >
                    {{ ingredient.product_name }}
                    ×
                    {{ formatNumber(ingredient.selling_units) }}
                  </span>
                </div>
              </td>

              <td class="right-align">
                <input
                  class="recipe-quantity-input"
                  type="number"
                  min="0"
                  step="1"
                  inputmode="numeric"
                  :disabled="disabled"
                  :value="quantityFor(recipe.id)"
                  @input="quantityChanged(recipe.id, $event.target.value)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p v-if="recipes.length" class="recipe-sales-help">
        These quantities populate the read-only Recipe Sales column. Direct Sales and
        Recipe Sales are added together before stock variance is calculated.
      </p>
    </div>
  </section>
</template>

<script>
export default {
  name: "StockRecipeSales",

  emits: ["update:modelValue"],

  props: {
    recipes: {
      type: Array,
      default: () => [],
    },

    modelValue: {
      type: Object,
      default: () => ({}),
    },

    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      open: true,
    };
  },

  computed: {
    activeRecipeCount() {
      return Object.values(this.modelValue).filter(
        (value) => Number.isFinite(Number(value)) && Number(value) > 0
      ).length;
    },
  },

  methods: {
    quantityFor(recipeId) {
      const value = Number(this.modelValue?.[recipeId]);

      return Number.isFinite(value) && value > 0 ? value : 0;
    },

    quantityChanged(recipeId, rawValue) {
      const value = Number(rawValue);

      const nextValue = Number.isFinite(value) && value > 0 ? value : 0;

      const next = {
        ...this.modelValue,
      };

      if (nextValue > 0) {
        next[recipeId] = nextValue;
      } else {
        delete next[recipeId];
      }

      this.$emit("update:modelValue", next);
    },

    formatNumber(value) {
      const number = Number(value);

      if (!Number.isFinite(number)) {
        return "0";
      }

      return new Intl.NumberFormat("en-GB", {
        maximumFractionDigits: 2,
      }).format(number);
    },
  },
};
</script>

<style scoped>
.recipe-sales-card {
  margin: 18px 0;
  overflow: hidden;
  border: 1px solid #cfd8dc;
  border-radius: 9px;
  background: #fff;
}

.recipe-sales-header {
  display: flex;
  width: 100%;
  padding: 14px 18px;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  border: 0;
  background: #455a64;
  color: #fff;
  cursor: pointer;
  text-align: left;
}

.recipe-sales-title,
.recipe-sales-header-right {
  display: flex;
  align-items: center;
}

.recipe-sales-title {
  gap: 12px;
}

.recipe-sales-title > span {
  display: flex;
  flex-direction: column;
}

.recipe-sales-title small {
  margin-top: 2px;
  color: #cfd8dc;
}

.recipe-sales-header-right {
  gap: 12px;
}

.recipe-count-chip {
  padding: 4px 9px;
  border-radius: 4px;
  background: #00897b;
  font-size: 0.82rem;
  font-weight: 700;
}

.recipe-sales-body {
  padding: 16px 18px;
}

.recipe-sales-table {
  min-width: 760px;
}

.recipe-name {
  min-width: 190px;
  font-weight: 700;
}

.ingredient-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.ingredient-summary span {
  padding: 4px 8px;
  border-radius: 999px;
  background: #eceff1;
  color: #455a64;
  font-size: 0.82rem;
}

.recipe-quantity-input {
  width: 110px !important;
  margin: 0 !important;
  text-align: right;
}

.recipe-sales-help {
  margin: 12px 0 0;
  color: #607d8b;
}
</style>
